import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import radioService from "../../api/services/radio";

/*
 * MRČ Radio, globalni audio player (v1, bez streaming servera).
 *
 * "Live" osećaj: server šalje anchor epohu + svoje vreme; pozicija u
 * playlisti se računa kao (sada - anchor) % ukupnoTrajanje, pa svi
 * posetioci čuju istu pesmu na istom mestu. Posle join-a ide sekvencijalno.
 *
 * Autoplay: browser dozvoljava zvuk tek posle korisničkog klika, pa se
 * puštanje uvek dešava iz UI akcije (prompt "Da" ili toggle u playeru).
 */

const STORAGE_KEY = "mrc-radio-pref"; // "on" | "off" | null (nije pitan)

const RadioContext = createContext(null);

export const useRadio = () => useContext(RadioContext);

export const RadioProvider = ({ children }) => {
  const audioRef = useRef(null);
  const syncRef = useRef(null); // { anchor, serverTime, fetchedAt }

  const [playlist, setPlaylist] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [preference, setPreference] = useState(() =>
    localStorage.getItem(STORAGE_KEY),
  );

  if (!audioRef.current && typeof Audio !== "undefined") {
    audioRef.current = new Audio();
    audioRef.current.preload = "none";
  }

  useEffect(() => {
    let cancelled = false;
    radioService
      .getPlaylist()
      .then((res) => {
        if (cancelled) return;
        setPlaylist(res.data || []);
        syncRef.current = {
          anchor: res.anchor,
          serverTime: res.serverTime,
          fetchedAt: Date.now(),
        };
      })
      .catch(() => {}); // radio je opciona stvar, bez playliste nema widgeta
    return () => {
      cancelled = true;
    };
  }, []);

  // "Radio sync", indeks i offset kao da program teče od anchor epohe
  const computeLivePosition = useCallback(() => {
    const sync = syncRef.current;
    const tracks = playlist;
    if (!sync || tracks.length === 0) return { index: 0, offset: 0 };

    const durations = tracks.map((t) => Number(t.duration) || 0);
    const total = durations.reduce((a, b) => a + b, 0);
    if (total <= 0) {
      // bez trajanja nema sinhronizacije, kreni od nasumične pesme
      return { index: Math.floor(Math.random() * tracks.length), offset: 0 };
    }

    const nowOnServer = sync.serverTime + (Date.now() - sync.fetchedAt);
    let pos = ((nowOnServer - sync.anchor) / 1000) % (total || 1);
    if (pos < 0) pos = 0;

    for (let i = 0; i < tracks.length; i += 1) {
      if (pos < durations[i]) return { index: i, offset: pos };
      pos -= durations[i];
    }
    return { index: 0, offset: 0 };
  }, [playlist]);

  const currentTrack = playlist[currentIndex] || null;

  const playTrack = useCallback(
    (index, offset = 0) => {
      const audio = audioRef.current;
      const track = playlist[index];
      if (!audio || !track) return;

      setCurrentIndex(index);
      audio.src = track.audioUrl;
      audio.currentTime = 0;
      const startPlayback = () => {
        if (offset > 1 && Number(track.duration)) {
          try {
            audio.currentTime = Math.min(offset, Number(track.duration) - 2);
          } catch {
            // neki formati ne dozvoljavaju seek pre metadata, kreni od početka
          }
        }
        audio
          .play()
          .then(() => setPlaying(true))
          .catch(() => setPlaying(false)); // autoplay blokiran, čeka klik
      };
      startPlayback();
    },
    [playlist],
  );

  const next = useCallback(() => {
    if (playlist.length === 0) return;
    playTrack((currentIndex + 1) % playlist.length, 0);
  }, [playlist.length, currentIndex, playTrack]);

  // Kraj pesme → sledeća
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return undefined;
    const onEnded = () => {
      if (playlist.length > 0) {
        playTrack((currentIndex + 1) % playlist.length, 0);
      }
    };
    audio.addEventListener("ended", onEnded);
    return () => audio.removeEventListener("ended", onEnded);
  }, [playlist.length, currentIndex, playTrack]);

  const start = useCallback(() => {
    const { index, offset } = computeLivePosition();
    playTrack(index, offset);
    setPreference("on");
    localStorage.setItem(STORAGE_KEY, "on");
  }, [computeLivePosition, playTrack]);

  const pause = useCallback(() => {
    audioRef.current?.pause();
    setPlaying(false);
    setPreference("off");
    localStorage.setItem(STORAGE_KEY, "off");
  }, []);

  const togglePlay = useCallback(() => {
    if (playing) {
      pause();
    } else {
      start();
    }
  }, [playing, pause, start]);

  const declinePrompt = useCallback(() => {
    setPreference("off");
    localStorage.setItem(STORAGE_KEY, "off");
  }, []);

  // Metapodaci na lock screen-u / u notifikaciji telefona
  useEffect(() => {
    if (!("mediaSession" in navigator) || !currentTrack) return;
    navigator.mediaSession.metadata = new window.MediaMetadata({
      title: currentTrack.title,
      artist: currentTrack.artist || "MRČ Radio",
      album: "MRČ Radio",
    });
    navigator.mediaSession.setActionHandler("play", () => togglePlay());
    navigator.mediaSession.setActionHandler("pause", () => pause());
    navigator.mediaSession.setActionHandler("nexttrack", () => next());
  }, [currentTrack, togglePlay, pause, next]);

  const value = useMemo(
    () => ({
      available: playlist.length > 0,
      playing,
      currentTrack,
      // prompt se prikazuje samo ako korisnik nikad nije odgovorio
      shouldPrompt: preference === null && playlist.length > 0,
      start,
      pause,
      togglePlay,
      next,
      declinePrompt,
    }),
    [
      playlist.length,
      playing,
      currentTrack,
      preference,
      start,
      pause,
      togglePlay,
      next,
      declinePrompt,
    ],
  );

  return (
    <RadioContext.Provider value={value}>{children}</RadioContext.Provider>
  );
};
